import { ReactComponent as VoucherAvailable } from 'assets/icons/voucher-available.svg';
import { ReactComponent as VoucherUnavailable } from 'assets/icons/voucher-unavailable.svg';
import Button from 'components/button';
import CounterCard from 'components/cards/CounterCard';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CertificationContext } from 'contexts/certifications';
import { SessionContext } from 'contexts/session';
import { buildContactSalesMailto } from 'utils/contactSales';
import AssignVoucherModal from '../AssignVoucherModal';
import { Tooltip } from 'antd';
import {
  ContactSalesButton,
  Container,
  NeedMoreVouchers,
  NoVoucherActions,
  NoVoucherCard,
  VoucherActions,
  VoucherCard,
  VoucherDetails,
  VoucherInfo,
  VoucherLabel,
  VoucherQuantity,
  Wrapper,
} from './styles';
import { useFlags } from 'launchdarkly-react-client-sdk';

const CertificationsCounters = (): JSX.Element => {
  const { fetching, counters, voucherSummary } = useContext(CertificationContext);
  const { isAccountOwner, account, isPastDue } = useContext(SessionContext);

  const [dataDisplay, setDataDisplay] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const mobileOnly = false;

  const roleLabel = isAccountOwner ? 'account' : 'team(s)';
  const voucherAssignmentV1 = useFlags()?.voucherAssignmentV1 ?? false;
  useEffect(() => {
    setDataDisplay(() => {
      return [
        {
          key: 'total_vouchers_assigned',
          tooltipLabel: null,
          counterTooltipLabel: `Total number of active vouchers assigned to users in your ${roleLabel} that can still be used`,
          disabled: true,
          counterLabel: 'Active vouchers assigned',
        },
        {
          key: 'percentage_of_users_with_vouchers',
          counterTooltipLabel: `Percentage of users in your ${roleLabel} who have active vouchers assigned`,
          counterLabel: 'Users with vouchers assigned',
          percentageKey: 'percentage_of_users_with_vouchers',
          disabled: true,
        },
        {
          key: 'total_certification_attempts_taken',
          tooltipLabel: null,
          counterTooltipLabel: `Total number of certification attempts in your ${roleLabel}, including both passed and failed`,
          disabled: true,
          counterLabel: 'Total certification attempts',
        },
        {
          key: 'total_passed_certification_attempts',
          tooltipLabel: null,
          counterTooltipLabel: `Total number of active certifications in your ${roleLabel} that are still valid`,
          disabled: true,
          counterLabel: 'Active certifications',
        },
      ];
    });
  }, []);

  const getQuantity = (key, percentageKey?) => {
    const value = counters[key];
    if (percentageKey) return `${Math.round(Number(value) || 0)}%`;
    return getValue(key, value);
  };

  const getLabel = (key, showQtyInLabel, label?, mobileCounterLabel?) => {
    if (showQtyInLabel) {
      return `${getQuantity(key)} ${label || key}`;
    }
    const currentLabel = label || key;
    return mobileOnly ? mobileCounterLabel || currentLabel : currentLabel;
  };

  const getValue = (key: string, value: number) => {
    return value ? value : '0';
  };

  const handleAssignVoucher = useCallback(() => {
    setAssignModalOpen(true);
  }, []);

  const handleContactSales = useCallback(() => {
    const subject = encodeURIComponent('Request for Additional Certification Vouchers');
    const body = encodeURIComponent(
      'Hi,\n\nI would like to request additional certification vouchers for my account.\n\nThank you'
    );
    window.location.href = buildContactSalesMailto(account, { subject, body });
  }, [account]);

  useEffect(() => {
    if (!voucherAssignmentV1 && assignModalOpen) {
      setAssignModalOpen(false);
    }
  }, [voucherAssignmentV1, assignModalOpen]);

  const hasVouchers = useMemo(() => {
    return (voucherSummary?.voucher_balance?.remaining ?? 0) > 0;
  }, [voucherSummary]);

  const CurrentVoucherActions = hasVouchers ? VoucherActions : NoVoucherActions;
  const CurrentVoucherCard = hasVouchers ? VoucherCard : NoVoucherCard;

  return (
    <Wrapper>
      <Container>
        {voucherAssignmentV1 && (
          <CurrentVoucherCard>
            <VoucherInfo>
              {hasVouchers ? (
                <VoucherAvailable
                  data-testid="voucher-available-icon"
                  aria-hidden="true"
                  focusable="false"
                />
              ) : (
                <VoucherUnavailable
                  data-testid="voucher-unavailable-icon"
                  aria-hidden="true"
                  focusable="false"
                />
              )}
              <VoucherDetails>
                <VoucherQuantity>{voucherSummary?.voucher_balance?.remaining ?? 0}</VoucherQuantity>
                <VoucherLabel>Vouchers available</VoucherLabel>
              </VoucherDetails>
            </VoucherInfo>
            <CurrentVoucherActions>
              {hasVouchers ? (
                <>
                  {isPastDue ? (
                    <Tooltip
                      title="Your account is no longer active. Please contact us to renew your plan and reinstate voucher assignment access."
                      placement="top"
                    >
                      <span>
                        <Button $primary size="small" disabled>
                          Assign vouchers
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button $primary size="small" onClick={handleAssignVoucher}>
                      Assign vouchers
                    </Button>
                  )}
                  <NeedMoreVouchers>
                    {'Need more vouchers?'}
                    <ContactSalesButton
                      onClick={handleContactSales}
                      data-testid="contact-sales-link"
                      aria-label="Contact sales to purchase more vouchers"
                      type="button"
                    >
                      Contact sales.
                    </ContactSalesButton>
                  </NeedMoreVouchers>
                </>
              ) : (
                <Button $primary size="small" onClick={handleContactSales}>
                  Contact sales to add vouchers
                </Button>
              )}
            </CurrentVoucherActions>
          </CurrentVoucherCard>
        )}

        {dataDisplay.map(
          ({
            key,
            counterLabel,
            counterTooltipLabel,
            percentageKey,
            mobileCounterLabel,
            hideCounter,
            showQtyInLabel,
          }) => {
            return !hideCounter ? (
              <CounterCard
                fetching={fetching}
                key={key}
                quantity={getQuantity(key, percentageKey)}
                label={getLabel(key, showQtyInLabel, counterLabel, mobileCounterLabel)}
                tooltipLabel={counterTooltipLabel}
              />
            ) : null;
          }
        )}
      </Container>

      {voucherAssignmentV1 && (
        <AssignVoucherModal
          visible={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          handleContactSales={handleContactSales}
        />
      )}
    </Wrapper>
  );
};

export default CertificationsCounters;
